import React, { useEffect, useState } from 'react';
import { UserCertificate, UserCertificationSummary } from '../../types/teamUserSummary';
import { courseService } from '../../services/courseService';
import { Certification } from '../../types/certification';

const CERTIFICATES_ICON_URL = 'https://storage.googleapis.com/rev-public/icons/Certificates.svg';

interface CertificatesWidgetProps {
  certificates?: UserCertificate[];
  activeCertifications?: UserCertificationSummary[];
}

export const CertificatesWidget: React.FC<CertificatesWidgetProps> = ({ 
  certificates = [], 
  activeCertifications = [] 
}) => {
  const [allCertifications, setAllCertifications] = useState<Certification[]>([]);

  // Fetch all certifications to include those with 0 progress
  useEffect(() => {
    const fetchAllCertifications = async () => {
      try {
        const certs = await courseService.getCertificationsWithCourses();
        setAllCertifications(certs);
      } catch (error) {
        // Error modal presented by API
      }
    };
    fetchAllCertifications();
  }, []);

  // Calculate progress for certifications
  const calculateProgress = (coursesCompleted: number | null | undefined, coursesTotal: number | null | undefined): number => {
    const completed = coursesCompleted ?? 0;
    const total = coursesTotal ?? 0;
    if (total === 0 || completed === 0) return 0;
    const percentage = Math.round((completed / total) * 100);
    return isNaN(percentage) ? 0 : percentage;
  };

  // Get status badge based on progress
  const getStatusBadge = (progress: number) => {
    if (progress >= 100) {
      return (
        <span className="bg-green-500 text-white px-3 py-1 rounded text-sm font-semibold uppercase border border-green-600">
          Completed
        </span>
      );
    } else if (progress > 0) {
      return (
        <span className="bg-tertiary text-white px-3 py-1 rounded text-sm font-semibold uppercase border" style={{ borderColor: '#4269a2' }}>
          In Progress
        </span>
      );
    } else {
      return (
        <span className="text-white px-3 py-1 rounded text-sm font-semibold uppercase border" style={{ backgroundColor: '#D44D5C', borderColor: '#D44D5C' }}>
          No Progress
        </span>
      );
    }
  };

  // Create maps for quick lookup
  const certificateIds = new Set(certificates.map(cert => cert.certificateId));
  const progressMap = new Map(
    activeCertifications.map(cert => [cert.certificationId, cert])
  );

  // Build display items from all certifications
  const displayItems = allCertifications
    .filter(cert => !certificateIds.has(cert.id)) // Exclude completed certificates (they're in certificates array)
    .map(cert => {
      const progressData = progressMap.get(cert.id);
      if (progressData) {
        // Has progress data - calculate from coursesCompleted/coursesTotal
        const calculatedProgress = calculateProgress(
          progressData.coursesCompleted, 
          progressData.coursesTotal
        );
        return {
          id: cert.id,
          name: cert.name,
          progress: calculatedProgress
        };
      } else {
        // No progress data - assume 0 progress
        return {
          id: cert.id,
          name: cert.name,
          progress: 0
        };
      }
    });

  // Add completed certificates
  const completedItems = certificates.map(cert => ({
    id: cert.certificateId,
    name: cert.name,
    progress: 100
  }));

  // Combine all items
  const allDisplayItems = [...completedItems, ...displayItems];

  return (
    <div className="bg-white flex w-full sm:min-w-60 flex-col px-5 py-4 rounded-[10px] h-full">
      <div className="flex items-center">
        <div className="flex items-center gap-3">
          <img
            src={CERTIFICATES_ICON_URL}
            alt="Certificates"
            className="w-8 h-8"
          />
          <div className="text-[#2D2D2D] text-base font-medium">
            Certificates
          </div>
        </div>
      </div>

      {allDisplayItems.length === 0 ? (
        <div className="flex flex-col mt-auto">
          <div className="pt-3">
            <p className="text-[#928881] text-base font-normal italic">0 certification progress</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col mt-auto">
          {allDisplayItems.map((item, index) => (
            <div key={item.id} className={`flex items-center justify-between gap-2 ${index === 0 ? 'mt-3' : 'mt-2'}`}>
              <div className="text-[#928881] text-base font-normal flex-1">
                {item.name}
              </div>
              {getStatusBadge(item.progress)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
